/* eslint-disable @typescript-eslint/no-explicit-any */
import { PointStyle } from "chart.js";
import { DisabledCrosshair } from "charts/src/helpers/chart/plugins/config";
import merge from "lodash/merge";

import { getBaseOptions } from "@/data/chart/base-options";
import { Lang } from "@/i18n-config";
import { getTranslations } from "@/utils/i18n/get-translations";

import {
  ForecastCategory,
  ForecastChartDataKeys,
  ForecastPeriod,
} from "../services/financial-forecast-chart";

type Params = {
  lang: Lang;
  period: ForecastPeriod;
  category: ForecastCategory;
};

export const getChartOptions = ({ lang, category, period }: Params) => {
  const { t } = getTranslations(lang);
  const baseOptions = getBaseOptions();
  const isAnnual = period === "annual";

  const categoryName =
    category === "eps"
      ? t.analysis.value.forecast.eps
      : t.analysis.value.forecast.revenue;

  const customOptions = {
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: "index" as const,
    },

    plugins: {
      crosshair: DisabledCrosshair,
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label: ForecastChartDataKeys = context.dataset.label;
            const value = context.parsed.y;
            const labelName =
              label === "pastData"
                ? `${t.analysis.value.forecast.reported}${categoryName}`
                : t.analysis.value.forecast[label];

            // hide forecast labels for last point of past data
            const isLastPastDataPoint =
              context.dataIndex ===
              // 判斷第一個非 null 的 index
              (context.dataset?.data.reduce((acc: number, curr: any) => {
                if (curr) return acc;
                return acc + 1;
              }, 0) ?? -1);
            const hideDatasetIndex = [1, 2, 3];

            if (
              isLastPastDataPoint &&
              hideDatasetIndex.includes(context.datasetIndex)
            )
              return "";

            return `${labelName} ${formatCurrency(value)}`;
          },
          labelColor: (context: any) => ({
            borderColor: "#333",
            backgroundColor: context.dataset.backgroundColor,
            borderRadius: 0,
          }),
        },
      },

      legend: {
        position: "bottom" as const,
        onClick: () => null,
        labels: {
          usePointStyle: true,
          generateLabels: () =>
            [
              categoryName,
              `${categoryName}${t.analysis.value.forecast.forecast}${t.analysis.value.forecast.range}`,
            ].map((label, i) => ({
              text: label,
              fontColor: "#52525B",
              pointStyle: (i === 0 ? "line" : "rect") as PointStyle,
              strokeStyle: i === 0 ? "#0369A1" : "#16A34A",
              fillStyle: i === 0 ? "#0369A1" : "#16A34A1A",
            })),
        },
      },
    },

    scales: {
      y: {
        ticks: {
          callback: formatCurrency,
          crossAlign: "center" as const,
        },
      },
      x: {
        ticks: {
          callback: isAnnual
            ? formatYear()
            : formatDate(lang === "en" ? "en-US" : "zh-CN"),
        },
      },
    },
  };

  return merge(baseOptions, customOptions);
};

const formatCurrency = (value: string | number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    notation: "compact",
    compactDisplay: "short",
  }).format(+value);

const formatDate = (locale: string) =>
  function (this: any, value: string | number) {
    const label = this.getLabelForValue(value);
    return new Intl.DateTimeFormat(locale, {
      month: "short",
      year: "numeric",
    })
      .format(new Date(label))
      .replace(/(\w+[A-Za-z])/, "$1.");
  };

const formatYear = () =>
  function (this: any, value: string | number) {
    const label = this.getLabelForValue(value);
    const year = new Date(label).getFullYear().toString();
    return year;
  };
